(() => {
  'use strict';
  const route = path => `${window.Shopify?.routes?.root || window.Tizico?.rootUrl || '/'}${path}`;
  const productControllers = new WeakMap();
  let drawer, opener, busy = false, refreshVersion = 0;
  const strings = () => window.Tizico?.strings || {};
  function lock() { document.body.classList.toggle('cart-modal-open', !!document.querySelector('dialog[open]')); }
  function close(dialog) { dialog.close(); }
  function setBusy(value) {
    busy = value;
    drawer.setAttribute('aria-busy', String(value));
    drawer.querySelectorAll('[data-drawer-quantity], [name="checkout"]').forEach(el => { el.disabled = value; });
  }
  async function refresh() {
    const version = ++refreshVersion;
    const response = await fetch(`${route('')}?sections=cart-drawer`, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('cart');
    const sections = await response.json();
    if (typeof sections['cart-drawer'] !== 'string') throw new Error('section');
    const html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    const content = html.querySelector('[data-cart-drawer-content]');
    if (!content) throw new Error('content');
    if (version !== refreshVersion) return;
    drawer.querySelector('[data-cart-drawer-content]').replaceWith(content);
    const count = content.querySelector('[data-drawer-count]');
    if (count) {
      const badge = document.getElementById('cart-count');
      if (badge) badge.textContent = count.dataset.drawerCount;
      document.getElementById('cart-open')?.setAttribute('aria-label', count.dataset.drawerCartLabel);
    }
  }
  async function openCart() {
    if (!drawer || busy) return;
    opener = document.activeElement;
    if (!drawer.open) drawer.showModal();
    lock();
    const status = drawer.querySelector('[data-cart-drawer-status]');
    status.textContent = drawer.dataset.loadingText;
    setBusy(true);
    let confirmed = false;
    try { await refresh(); confirmed = true; status.textContent = ''; }
    catch (error) { console.warn('Tizico cart refresh:', error.message); status.textContent = drawer.dataset.errorText; }
    finally { setBusy(false); if (!confirmed) drawer.querySelectorAll('[name="checkout"]').forEach(button => { button.disabled = true; }); }
  }
  async function changeLine(key, quantity) {
    if (busy || !Number.isInteger(quantity) || quantity < 0) return;
    setBusy(true);
    const status = drawer.querySelector('[data-cart-drawer-status]');
    status.textContent = drawer.dataset.loadingText;
    let confirmed = false;
    try {
      const response = await fetch(route('cart/change.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || drawer.dataset.updateError);
      await refresh();
      confirmed = true;
      status.textContent = '';
    } catch { status.textContent = drawer.dataset.updateError; }
    finally {
      setBusy(false);
      if (!confirmed) drawer.querySelectorAll('[name="checkout"]').forEach(button => { button.disabled = true; });
    }
  }
  function initStickyPurchase(section, form) {
    const bar = section.querySelector('[data-sticky-purchase]');
    const spacer = section.querySelector('[data-purchase-spacer]');
    if (!form || !bar || !spacer) return () => {};
    document.body.append(spacer);
    let frame = 0;
    let disposed = false;
    const editable = () => document.activeElement?.matches('input:not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]):not([type="hidden"]), textarea, select, [contenteditable="true"]');
    const keyboardOpen = () => editable() && (window.matchMedia('(max-width: 760px)').matches || (window.visualViewport && window.innerHeight - window.visualViewport.height > 100));
    const size = () => {
      if (bar.hidden) return;
      const height = Math.ceil(bar.getBoundingClientRect().height);
      spacer.style.height = `${height}px`;
      document.documentElement.style.setProperty('--tizico-purchase-offset', `${height}px`);
    };
    const update = () => {
      frame = 0;
      if (disposed) return;
      const obstructed = !!document.querySelector('dialog[open]') || keyboardOpen();
      const past = form.getBoundingClientRect().bottom <= 0;
      const visible = !obstructed && (past || bar.contains(document.activeElement));
      bar.hidden = !visible;
      spacer.hidden = !visible;
      if (visible) size();
      else document.documentElement.style.removeProperty('--tizico-purchase-offset');
    };
    const schedule = () => { if (!frame && !disposed) frame = window.requestAnimationFrame(update); };
    const intersection = 'IntersectionObserver' in window ? new IntersectionObserver(schedule, { threshold: 0 }) : null;
    intersection?.observe(form);
    if (!intersection) window.addEventListener('scroll', schedule, { passive: true });
    const dialogs = new MutationObserver(schedule);
    dialogs.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
    const resize = 'ResizeObserver' in window ? new ResizeObserver(size) : null;
    resize?.observe(bar);
    window.addEventListener('resize', schedule, { passive: true });
    window.visualViewport?.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', schedule);
    update();
    return () => {
      disposed = true;
      if (frame) window.cancelAnimationFrame(frame);
      intersection?.disconnect();
      dialogs.disconnect();
      resize?.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      document.removeEventListener('focusin', schedule);
      document.removeEventListener('focusout', schedule);
      document.documentElement.style.removeProperty('--tizico-purchase-offset');
      spacer.remove();
    };
  }
  function initProduct(section) {
    if (section.dataset.essentialsReady) return;
    section.dataset.essentialsReady = 'true';
    const select = section.querySelector('[data-variant-select]');
    const form = section.querySelector('[data-tizico-product-form]');
    const choices = section.querySelector('[data-variant-buttons]');
    if (choices) choices.hidden = false;
    if (select && (choices || select.options.length === 1)) select.closest('.product-variant-field').hidden = true;
    const sync = () => {
      const button = section.querySelector('[data-product-submit]');
      const pending = form?.getAttribute('aria-busy') === 'true' || !!document.querySelector('[data-product-bundle][aria-busy="true"]');
      const unavailable = !button || button.disabled;
      section.querySelectorAll('[data-buy-now]').forEach(buyNow => { buyNow.disabled = pending || unavailable; });
      const buyLabel = section.querySelector('[data-sticky-buy-label]');
      const stickyBuy = section.querySelector('[data-sticky-buy-now]');
      if (buyLabel && stickyBuy) buyLabel.textContent = pending ? strings().adding : unavailable ? strings().sold_out : stickyBuy.dataset.buyLabel;
      const sticky = section.querySelector('[data-sticky-add]');
      if (sticky) { sticky.disabled = pending || unavailable; sticky.textContent = pending ? strings().adding : section.querySelector('[data-add-label]')?.textContent; }
      const price = section.querySelector('[data-sticky-price]');
      if (price) price.textContent = section.querySelector('[data-product-price]')?.textContent;
      section.querySelectorAll('[data-variant-choice]').forEach(choice => { choice.setAttribute('aria-pressed', String(choice.dataset.variantChoice === select?.value)); choice.disabled = pending; });
      const express = section.querySelector('[data-product-express]');
      if (express) express.hidden = pending || unavailable;
      const stock = section.querySelector('[data-product-stock]');
      if (stock && !pending) stock.textContent = unavailable ? strings().sold_out : stock.dataset.availableText;
    };
    section.addEventListener('tizico:variant-changed', sync);
    const stateObserver = new MutationObserver(sync);
    if (form) stateObserver.observe(form, { attributes: true, attributeFilter: ['aria-busy'] });
    const submit = section.querySelector('[data-product-submit]');
    if (submit) stateObserver.observe(submit, { attributes: true, attributeFilter: ['disabled'] });
    sync();
    const disposeSticky = initStickyPurchase(section, form);
    const zoom = section.querySelector('[data-zoom-dialog]');
    if (zoom) {
      zoom.addEventListener('close', () => { lock(); opener?.focus(); });
      zoom.addEventListener('click', event => { if (event.target === zoom || event.target.closest('[data-zoom-close]')) close(zoom); });
    }
    const gallery = section.querySelector('[data-product-gallery]');
    const thumbnails = section.querySelector('[data-gallery-thumbnails]');
    const revealThumbnail = () => {
      const selected = thumbnails?.querySelector('[aria-pressed="true"]');
      if (!selected) return;
      const bounds = selected.getBoundingClientRect(), viewport = thumbnails.getBoundingClientRect();
      if (bounds.top < viewport.top) thumbnails.scrollTop += bounds.top - viewport.top;
      else if (bounds.bottom > viewport.bottom) thumbnails.scrollTop += bounds.bottom - viewport.bottom;
      if (bounds.left < viewport.left) thumbnails.scrollLeft += bounds.left - viewport.left;
      else if (bounds.right > viewport.right) thumbnails.scrollLeft += bounds.right - viewport.right;
    };
    const galleryObserver = new MutationObserver(revealThumbnail);
    if (thumbnails) galleryObserver.observe(thumbnails, { subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });
    const navigateThumbnails = event => {
      const current = event.target.closest('[data-gallery-select]');
      if (!current) return;
      const buttons = [...thumbnails.querySelectorAll('[data-gallery-select]')];
      let index = buttons.indexOf(current);
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') index = (index + 1) % buttons.length;
      else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') index = (index - 1 + buttons.length) % buttons.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = buttons.length - 1;
      else return;
      event.preventDefault();
      buttons[index]?.click();
      buttons[index]?.focus({ preventScroll: true });
      revealThumbnail();
    };
    thumbnails?.addEventListener('keydown', navigateThumbnails);
    let touch;
    gallery?.addEventListener('touchstart', event => { const point = event.touches[0]; touch = { x: point.clientX, y: point.clientY }; }, { passive: true });
    gallery?.addEventListener('touchend', event => {
      if (!touch || event.target.closest('video, iframe, model-viewer, button')) return;
      const point = event.changedTouches[0], dx = point.clientX - touch.x, dy = point.clientY - touch.y;
      touch = null;
      if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy)) return;
      const thumbs = [...section.querySelectorAll('[data-gallery-select]')];
      const index = thumbs.findIndex(item => item.getAttribute('aria-pressed') === 'true');
      thumbs[(index + (dx < 0 ? 1 : -1) + thumbs.length) % thumbs.length]?.click();
    }, { passive: true });
    productControllers.set(section, () => {
      disposeSticky();
      stateObserver.disconnect();
      galleryObserver.disconnect();
      section.removeEventListener('tizico:variant-changed', sync);
      thumbnails?.removeEventListener('keydown', navigateThumbnails);
      delete section.dataset.essentialsReady;
    });
  }
  function init() {
    drawer = document.getElementById('tizico-cart-drawer');
    drawer?.addEventListener('close', () => { lock(); opener?.focus(); });
    document.querySelectorAll('[data-tizico-product]').forEach(initProduct);
    document.addEventListener('tizico:cart-added', openCart);
    document.addEventListener('click', event => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('#cart-open') && drawer && !event.ctrlKey && !event.metaKey && !event.shiftKey) { event.preventDefault(); openCart(); }
      if (drawer?.open && (target === drawer || target.closest('[data-cart-close]'))) close(drawer);
      const remove = target.closest('[data-drawer-remove]');
      if (remove) { event.preventDefault(); changeLine(remove.dataset.lineKey, 0); }
      const choice = target.closest('[data-variant-choice]');
      if (choice) { const select = choice.closest('[data-tizico-product]').querySelector('[data-variant-select]'); select.value = choice.dataset.variantChoice; select.dispatchEvent(new Event('change', { bubbles: true })); }
      const image = target.closest('[data-product-zoom]');
      if (image) {
        const dialog = image.closest('[data-tizico-product]').querySelector('[data-zoom-dialog]');
        if (!dialog) return;
        event.preventDefault(); opener = image;
        const zoomImage = dialog.querySelector('[data-zoom-image]'); zoomImage.src = image.href; zoomImage.alt = image.querySelector('img')?.alt || '';
        dialog.showModal(); lock();
      }
    });
    document.addEventListener('change', event => {
      const input = event.target;
      if (input.matches('[data-drawer-quantity]') && input.reportValidity()) changeLine(input.dataset.lineKey, Number(input.value));
    });
    document.addEventListener('shopify:section:load', () => document.querySelectorAll('[data-tizico-product]').forEach(initProduct));
    document.addEventListener('shopify:section:unload', event => {
      const sections = [...event.target.querySelectorAll('[data-tizico-product]')];
      if (event.target.matches?.('[data-tizico-product]')) sections.push(event.target);
      sections.forEach(section => { productControllers.get(section)?.(); productControllers.delete(section); });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();

